/**
 * Path helpers for the Pirsch dashboard.
 *
 * Since 2026-08 the runtime reports the real public path instead of the
 * internal id-path (`/f<folder>/pr<project>/pa<page>`) — see the normalizeSlugPath
 * block in `files/_app_dev/js/lib.js`, which this file must byte-match: a
 * dashboard pattern that differs by one slash finds nothing.
 *
 * Spec: docs/superpowers/specs/2026-06-17-pirsch-analytics-lineage-design.md
 */

// FolderSettings.vue marks the account root with this instead of a slug path.
export const ROOT_FOLDER_SENTINEL = '#__ROOT_FOLDER__#';

/**
 * Leading slash, no trailing slash, no duplicate slashes. Anything unusable
 * (empty, root sentinel, non-string) becomes the account root.
 */
export function normalizeSlugPath(path) {
  if (typeof path !== 'string' || path === ROOT_FOLDER_SENTINEL) return '/';
  const trimmed = path.replace(/^\/+|\/+$/g, '');
  if (!trimmed) return '/';
  return `/${trimmed}`.replace(/\/{2,}/g, '/');
}

/**
 * The public path of a folder/project/page row, read from its `lookup_entries`
 * relation (item_lookup). Returns null when the row carries no entry — the
 * caller then has no path to query and must skip, not guess.
 */
export function slugPathOfItem(item) {
  const entry = item && item.lookup_entries && item.lookup_entries[0];
  const slugPath = entry && entry.slug_path;
  return typeof slugPath === 'string' && slugPath !== '' ? normalizeSlugPath(slugPath) : null;
}

/**
 * Escape a literal string for safe embedding in a regular expression.
 */
export function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the anchored Pirsch path pattern for a set of slug paths (the
 * project's current URL plus every URL it held before, from
 * project_url_history). Each path matches itself and its subtree, nothing
 * else. Any input that normalizes to the root path collapses the pattern
 * to '^/' (match everything on the domain).
 */
export function buildPathPattern(paths) {
  const normalized = [...new Set(
    (paths || [])
      .filter((p) => typeof p === 'string' && p)
      .map((p) => normalizeSlugPath(p)),
  )];
  if (!normalized.length || normalized.includes('/')) return '^/';
  return `^(${normalized.map(escapeRegex).join('|')})($|/)`;
}

const isUnder = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`);
const day = (iso) => (iso ? String(iso).slice(0, 10) : null);

/**
 * Split a project's URLs into Pirsch query segments.
 *
 * `ownRows` are the project's project_url_history rows in its current group,
 * `otherRows` rows of other projects in that group (own rows are ignored).
 * A path nobody else ever held is queried over the whole range. A path that
 * another project held too (same path or a project below it) only counts for
 * the project's own tenure, so a later project on the old URL does not leak
 * into this dashboard, and vice versa. Pirsch filters by day, so the hand-over
 * day itself still counts for both.
 *
 * Returns { current, oldPaths, segments: [{ paths, from, to }] }; from/to are
 * 'YYYY-MM-DD' or null (open).
 */
export function urlLineage(currentPath, ownRows, otherRows) {
  const current = normalizeSlugPath(currentPath);
  const own = (ownRows || []).filter((r) => r && typeof r.slug_path === 'string');
  const ownIds = new Set(own.map((r) => r.project_id));
  const others = (otherRows || [])
    .filter((r) => r && typeof r.slug_path === 'string' && !ownIds.has(r.project_id));

  const free = [];
  const bounded = [];
  own.forEach((row) => {
    const path = normalizeSlugPath(row.slug_path);
    const clashes = others.filter((o) => isUnder(normalizeSlugPath(o.slug_path), path));
    const from = clashes.some((o) => o.first_seen < row.first_seen) ? day(row.first_seen) : null;
    const to = (row.last_seen && clashes.some((o) => o.first_seen > row.first_seen))
      ? day(row.last_seen) : null;
    if (from || to) bounded.push({ paths: [path], from, to });
    else free.push(path);
  });
  if (!own.some((r) => normalizeSlugPath(r.slug_path) === current)) free.push(current);

  const freePaths = [...new Set(free)].sort((a, b) => (b === current) - (a === current));
  const segments = freePaths.length ? [{ paths: freePaths, from: null, to: null }] : [];
  bounded
    .filter((seg) => !freePaths.includes(seg.paths[0]))
    .forEach((seg) => segments.push(seg));

  const oldPaths = [...new Set(own.map((r) => normalizeSlugPath(r.slug_path)))]
    .filter((p) => p !== current);
  return { current, oldPaths, segments };
}

/**
 * Map a path reported under one of the project's former URLs onto its current
 * URL, so the rows of a renamed page add up with the new ones.
 */
export function rewriteToCurrent(path, lineage) {
  if (!lineage || typeof path !== 'string') return path;
  const { current } = lineage;
  if (isUnder(path, current)) return path;
  const old = [...lineage.oldPaths]
    .sort((a, b) => b.length - a.length)
    .find((p) => p !== '/' && isUnder(path, p));
  return old ? `${current}${path.slice(old.length)}` : path;
}
